import React, { useRef, useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import avatecLogo from '../../assets/logos/avatec-white.png';
import './Slide3.css';

/* ─── Card Data ─── */
const solutionCards = [
  {
    id: 'automation',
    title: 'Marine Automation Systems',
    subtitle: 'Integrated vessel control and monitoring',
    detail: 'Complete automation solutions for engine rooms, cargo, ballast, and power management systems.',
    image: '/images/capabilities/automation.jpg',
    size: 'hero',
  },
  {
    id: 'navigation',
    title: 'Radio Navigation',
    subtitle: 'Precision positioning and radar systems',
    detail: 'ECDIS, GMDSS, radar, AIS, and gyrocompass supply, installation, and service.',
    image: '/images/capabilities/navigation.jpg',
    size: 'medium',
  },
  {
    id: 'connectivity',
    title: 'Onboard Connectivity',
    subtitle: 'VSAT, Fleet Broadband, and IoT solutions',
    detail: 'High-throughput satellite connectivity for crew welfare, fleet operations, and remote diagnostics.',
    image: '/images/capabilities/connectivity.jpg',
    size: 'medium',
  },
  {
    id: 'liquid',
    title: 'Liquid Level Gauge Systems',
    subtitle: 'Tank monitoring and cargo measurement',
    detail: 'Precision level gauging for ballast, cargo, and fuel oil tanks.',
    image: '/images/capabilities/liquid-level.jpg',
    size: 'small',
  },
  {
    id: 'fire',
    title: 'Fire Alarm Systems',
    subtitle: 'Detection, suppression, and compliance',
    detail: 'Fire and gas detection systems with full classification society approval.',
    image: '/images/capabilities/fire-alarm.jpg',
    size: 'small',
  },
  {
    id: 'safety',
    title: 'Safety at Sea',
    subtitle: 'LSA equipment and SOLAS compliance',
    detail: 'Life-saving appliances, EPIRBs, SARTs, and immersion suit servicing.',
    image: '/images/capabilities/safety.jpg',
    size: 'small',
  },
  {
    id: 'boiler',
    title: 'Boiler & IGG & IGS Repair & Inspection',
    subtitle: 'Combustion systems and inert gas service',
    detail: 'Expert repair, overhaul, and inspection of boiler, IGG, and IGS systems.',
    image: '/images/capabilities/boiler.jpg',
    size: 'small',
  },
];

const serviceCards = [
  {
    id: 'automation',
    title: 'Annual Surveys',
    subtitle: 'Radio survey & compliance',
    detail:
      'Authorized radio survey, including GMDSS, AIS, EPIRB, SART, SSAS. Official VDR performance test by maker certified service partner. Gyro compass overhaul for all models by highly skilled professionals. Magnetic compass onboard and remote calibration by certified deviator.',
    image: '/images/service-support/annual-surveys.jpg',
    size: 'hero',
  },
  {
    id: 'navigation',
    title: 'Onboard Services Worldwide',
    subtitle: 'Global service coverage',
    detail:
      'Reliable onboard maintenance and remote monitoring of your critical assets with expertise of our engineers, to keep your vessel running safely and efficiently. Making your daily work easier by speeding up problem resolution and maximising uptime of your priority operations.',
    image: '/images/service-support/engineering-site.jpg',
    size: 'medium',
  },
  {
    id: 'connectivity',
    title: 'Maintenance Contracts',
    subtitle: 'Predictable lifecycle service',
    detail:
      'Our service agreements provide the capability to predict long-term maintenance costs and cash-flow. Systematic planning increases the efficiency of onboard visits by performing the appropriate tasks, at the appropriate time, based on the equipment life-cycle status.',
    image: '/images/service-support/maintenance-contracts.jpg',
    size: 'medium',
  },
  {
    id: 'liquid',
    title: 'Accounting Authority',
    subtitle: 'AAIC & Inmarsat activation',
    detail:
      'Providing Maritime Radio Accounting Authority services with our AAIC code EE05 or in partnership with other major Accounting Authorities, we deliver AAIC and Inmarsat activation service for most flag states.',
    image: '/images/service-support/accounting-authority.jpg',
    size: 'small',
  },
  {
    id: 'fire',
    title: 'Nautical Charts & Publications',
    subtitle: 'Electronic & paper charts',
    detail:
      "Supply of electronic and paper charts with the coverage of world's commercial shipping routes and ports, from authorized reseller to manage single vessel or complete fleet inventory and regulatory compliance.",
    image: '/images/service-support/nautical-charts.jpg',
    size: 'small',
  },
  {
    id: 'safety',
    title: 'Ship Design & Newbuilding',
    subtitle: 'Design, engineering, delivery',
    detail:
      'With combined knowledge and experience in the areas of design, engineering, project management, construction, installation and commissioning, we deliver innovative designs and sustainable solutions by meeting IMO regulations.',
    image: '/images/service-support/ship-design.jpg',
    size: 'small',
  },
  {
    id: 'boiler',
    title: 'Remote Support & Diagnostics',
    subtitle: 'Real-time troubleshooting',
    detail:
      'Our service tools allow to perform diagnostic troubleshooting by viewing in real time what is occurring on systems onboard. Support personnel can collaborate with end users, assess and perform quick, decisive diagnostics during fault finding.',
    image: '/images/service-support/remote-support.jpg',
    size: 'small',
  },
];

/* ─── Signal Dots ─── */
function SignalDots({ count = 3 }) {
  return (
    <div className="s3-signal-dots">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="s3-signal-dot" style={{ animationDelay: `${i * 0.3}s` }} />
      ))}
    </div>
  );
}

/* ─── Bento Card ─── */
function BentoCard({ card, index, reduceMotion }) {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (reduceMotion || !cardRef.current) return;
    const el = cardRef.current;

    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--mouse-x', `${x}%`);
      el.style.setProperty('--mouse-y', `${y}%`);
    };

    el.addEventListener('mousemove', handleMove);
    return () => el.removeEventListener('mousemove', handleMove);
  }, [reduceMotion]);

  const fadeUp = reduceMotion
    ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
    : {
        initial: { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, delay: 0.15 + index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
      };

  return (
    <motion.div
      ref={cardRef}
      className={`s3-card s3-card--${card.size} s3-card--${card.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...fadeUp}
    >
      {/* Background image */}
      <div
        className="s3-card-bg"
        style={{ backgroundImage: `url(${card.image})` }}
      />
      {/* Overlay */}
      <div className="s3-card-overlay" />
      {/* Glow border on hover */}
      <div className="s3-card-glow" />

      {/* Content */}
      <div className="s3-card-content">
        <SignalDots count={card.size === 'hero' ? 4 : 3} />
        <h3 className="s3-card-title">{card.title}</h3>
        <p className="s3-card-subtitle">{card.subtitle}</p>

        {/* Hidden detail on hover */}
        <div className={`s3-card-detail ${isHovered ? 's3-card-detail--visible' : ''}`}>
          <p>{card.detail}</p>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Background Radar ─── */
function BgRadar({ reduceMotion }) {
  return (
    <svg className="s3-bg-radar" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
      {[90, 150, 210].map((r, i) => (
        <circle key={i} cx="250" cy="250" r={r} fill="none"
          stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"
          strokeDasharray={i % 2 === 0 ? 'none' : '4 8'} />
      ))}
      <line x1="250" y1="50" x2="250" y2="450" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      <line x1="50" y1="250" x2="450" y2="250" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      {!reduceMotion && (
        <line x1="250" y1="250" x2="250" y2="50" stroke="rgba(255,255,255,0.08)" strokeWidth="0.8">
          <animateTransform attributeName="transform" type="rotate"
            from="0 250 250" to="360 250 250" dur="22s" repeatCount="indefinite" />
        </line>
      )}
      <circle cx="250" cy="250" r="3" fill="rgba(255,255,255,0.10)" />
    </svg>
  );
}

/* ─── Main Slide ─── */
function Slide3({ variant = 'solutions' }) {
  const prefersReduced = useReducedMotion();
  const reduceMotion = !!prefersReduced;
  const gridRef = useRef(null);
  const isServices = variant === 'services';

  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => { setIsVisible(true); }, []);

  const activeCards = isServices ? serviceCards : solutionCards;

  return (
    <div className="slide slide3">
      <img src={avatecLogo} alt="Avatec Marine" className="slide-logo" />

      {/* Background layers */}
      <div className="s3-bg-layers">
        <svg className="s3-blueprint" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="s3Grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.025)" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#s3Grid)" />
        </svg>
        <BgRadar reduceMotion={reduceMotion} />
        <div className="s3-noise" />
      </div>

      {/* Section number */}
      <motion.span className="s3-section-number"
        {...(reduceMotion
          ? { initial: { opacity: 0.04 }, animate: { opacity: 0.04 } }
          : { initial: { opacity: 0, x: 30 }, animate: isVisible ? { opacity: 0.04, x: 0 } : {}, transition: { duration: 1, delay: 0.1 } }
        )}>
        03
      </motion.span>

      {/* Header */}
      <div className="s3-header">
        <div className="s3-header-title">{isServices ? 'SERVICES' : 'SOLUTIONS'}</div>
      </div>

      {/* Bento Grid */}
      <div className="s3-container">
        <div className="s3-bento-grid" ref={gridRef}>
          {activeCards.map((card, i) => (
            <BentoCard key={`${variant}-${card.id}`} card={card} index={i} reduceMotion={reduceMotion} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Slide3;
